import { appstr } from "../utils/appstr";
import { enumDocCreateStep, kAccountTrial, kAccountTrialExpired, kAdminUser } from "../constants";
import { QAlert } from "./QAlert";
import { QRFC } from "./QRFC";
import { QDataRefreshRFC } from "./QDataRefreshRFC";
import { Globals } from "./Globals";
import {
  gNoQueryResultsTitle,
  gNoQueryResultsAdmin,
  gNoQueryResultsUser,
  gMissingJobIdTitle,
  gMissingJobIdAdmin,
  gMissingJobIdUser,
  gMappingScenarioStrings,
  gUseBannerErrorAlert
} from "../whitelabel/brand";

export class QSvrChartQuery extends QRFC {
  constructor() {
    super();
  }

  InitServerChartQuery(callbackObj) {
    this.InitRemoteCall(callbackObj, "POST", true, false);
    this.fAllowStacked = true;
  }

  GetCallName() {
    return "qquerysvrchart";
  }

  ApplyMappingUpdate(wsMgr, doc, newMappingField) {
    var syncRFC = new QDataRefreshRFC();
    syncRFC.fReactCallback = true;
    syncRFC.InitQDataRefreshQuery({
      HandleResponse: success => {
        if (success) {
          doc.DoChartDataQuery(null, null, false, true);
        }
      }
    });
    syncRFC.SetParamsExplicit(
      wsMgr.GetAccountName(),
      wsMgr.GetUserName(),
      "isolvedUpdateSupervisorMapping",
      JSON.stringify({ field: newMappingField }),
      doc.GetChartID()
    );
    Globals._rfcMgr.QueueRFC(syncRFC);
  }

  // Persists "Keep mapping" so a dismissible warning doesn't reappear on reload.
  DismissMappingWarning(wsMgr, doc, scenario) {
    var syncRFC = new QDataRefreshRFC();
    syncRFC.InitQDataRefreshQuery(wsMgr);
    syncRFC.SetParamsExplicit(
      wsMgr.GetAccountName(),
      wsMgr.GetUserName(),
      "isolvedDismissMappingWarning",
      JSON.stringify({ scenario }),
      doc.GetChartID()
    );
    Globals._rfcMgr.QueueRFC(syncRFC);
  }

  HandleMappingWarning(wsMgr, doc, response) {
    var info = response["info"];
    var scenario = info["mappingWarningCode"];
    if (!scenario || Globals._publishRef) return;

    // Every successful query re-attaches the code, so the warning shows once per doc.
    var shownScenarios = (doc._mappingAlertScenariosShown ??= new Set());
    if (shownScenarios.has(scenario)) return;
    shownScenarios.add(scenario);

    var isAdminUser = Globals._accountInfo.GetUserRole() === kAdminUser;
    this.FireMappingAlert(wsMgr, doc, scenario, isAdminUser);
  }

  FireMappingAlert(wsMgr, doc, scenario, isAdminUser) {
    var stringSet = gMappingScenarioStrings[scenario];
    var message = stringSet
      ? appstr(isAdminUser ? stringSet.admin : stringSet.user)
      : appstr("querybuilddataerr");

    var alert = new QAlert();
    if (isAdminUser && stringSet?.updateTarget) {
      alert.InitReactConfirmAlert(
        () => this.ApplyMappingUpdate(wsMgr, doc, stringSet.updateTarget),
        stringSet.dismissible ? () => this.DismissMappingWarning(wsMgr, doc, scenario) : () => {},
        appstr("Update mapping"),
        appstr(stringSet.deny)
      );
      alert.CommonInit(wsMgr, null, appstr(stringSet.title), message, undefined, undefined, {
        bannerErrorAlert: true
      });
    } else {
      var header = gUseBannerErrorAlert && stringSet ? appstr(stringSet.title) : appstr("Alert");
      alert.InitAlert(header, message, null, wsMgr, true, undefined, {
        bannerErrorAlert: gUseBannerErrorAlert || undefined
      });
    }
  }

  SetParams(orgName, user, topboxid, chartID, levels, querySettings) {
    this.AddParameter("org", orgName);
    this.AddParameter("user", user);
    this.AddParameter("topboxid", topboxid);
    this.AddParameter("chartID", chartID);
    this.AddParameter("levels", levels);
    this.AddParameter("querySettings", querySettings);
  }

  DoTransactionComplete(response, doc) {
    response = JSON.parse(response);
    var success = response["success"];
    var wsMgr = doc.GetDocMgr().fWSMgr;
    if (success) {
      if (doc.IsServerChart() && doc.IsLiveChart()) {
        doc.SvrChartQueryResult(success, response);
        var info = response["info"];
        if (info["updatedUser"]) {
          wsMgr.UpdateUserInfo(info["updatedUser"]);
        }
        this.HandleMappingWarning(wsMgr, doc, response);
      } else {
        const didSucceed = doc.StaticChartQueryResult(success, response);
        if (!didSucceed) {
          return;
        }
        this.HandleMappingWarning(wsMgr, doc, response);
        wsMgr.AdvanceBuilderChart(
          enumDocCreateStep.applyoverrides,
          Globals._svrChartMgr["metadata"],
          Globals._svrChartMgr["chartdata"],
          "dynloading",
          doc
        );
        return;
      }
    } else {
      wsMgr.EndProgress();
      wsMgr.SignalDocChanged();
      wsMgr.FinishDynChartLogin();
      let r = appstr("querybuilddataerr");
      let alertHeader = gUseBannerErrorAlert ? r : appstr("Alert");
      let alertMessage = null;
      let details = "";
      let info = response["info"];
      let isAdminUser = Globals._accountInfo.GetUserRole() === kAdminUser;
      let mappingAlertFired = false;
      switch (response["code"]) {
        case "0004":
          wsMgr.BroadcastTopic("TooManyResults", {});
          var max = info["max"];
          if (typeof max === "number") {
            max = max.toLocaleString();
          }

          r = "";
          if (Globals._publishRef) {
            details = `${appstr(
              "You’ve exceeded the max number of boxes allowed for export. Please reduce the scope of your export, or attempt an export from a different part of your organization, so that the number of boxes will not exceed your limit"
            )} [${max}]. ${appstr("Administrators can update this limit in the Chart Settings: Presentation panel, though OrgChart does not recommend exporting charts with more than 20,000 records")}`;
          } else {
            details = `${appstr("You’ve reached the maximum limit of")} ${max} ${appstr(
              "boxes for a custom chart. Try building the chart from a smaller section of your organization or apply filters to reduce the number of boxes."
            )} \n\n${appstr("Admins can adjust this limit in Chart Settings > Presentation Panel, but OrgChart recommends keeping charts under 20,000 records for best performance.")}`;
          }
          break;
        case "0005":
          var max = info["max"];
          details =
            appstr("querytoomanyresults4") +
            "(" +
            appstr("Max") +
            " " +
            max +
            "). " +
            appstr("pleaseReduceNumberLevels");

          // const requestedLevels = doc.GetRequestedLevels();
          // if (requestedLevels > 1) {
          //   const scm = doc.GetSubChartMgr();
          //   const curSC = scm.GetCurrentSubChart();
          //   const subRef = curSC?.GetTopDBObjRef();

          //   doc.SetRequestedLevels(requestedLevels - 1);
          //   doc.DoChartDataQuery(subRef);
          // }
          break;
        case "0009":
          if (gUseBannerErrorAlert) {
            alertHeader = appstr(gNoQueryResultsTitle);
          }
          details = appstr(isAdminUser ? gNoQueryResultsAdmin : gNoQueryResultsUser);
          break;
        case "0036":
          this.FireMappingAlert(wsMgr, doc, "missingManager", isAdminUser);
          mappingAlertFired = true;
          break;
        case "0037":
          this.FireMappingAlert(wsMgr, doc, "missingSupervisor", isAdminUser);
          mappingAlertFired = true;
          break;
        case "0038":
          this.FireMappingAlert(wsMgr, doc, "noRelationships", isAdminUser);
          mappingAlertFired = true;
          break;
        case "0039":
          this.FireMappingAlert(wsMgr, doc, "incompleteManager", isAdminUser);
          mappingAlertFired = true;
          break;
        case "0040":
          this.FireMappingAlert(wsMgr, doc, "incompleteSupervisor", isAdminUser);
          mappingAlertFired = true;
          break;
        case "0035":
          if (gUseBannerErrorAlert) {
            alertHeader = appstr(gMissingJobIdTitle);
          }
          details = appstr(isAdminUser ? gMissingJobIdAdmin : gMissingJobIdUser);
          break;
        case "0033":
          details = appstr("importfiltercharterr");
          break;
        case "0010":
          details = appstr("servercharterr");
          break;
        case "0011":
          return;
        case "0012":
          if (
            Globals._accountInfo.GetUserRole() === kAdminUser &&
            !(Globals._accountInfo.GetAccountStatus() === kAccountTrialExpired)
          ) {
            details = Globals._mobileDevice
              ? appstr("No chart data loaded mobile")
              : appstr("No chart data loaded - select the Refresh Data Now option to load data");
            wsMgr.ViewerNeedsRefreshAlert(details, response["chartID"]);
            return;
          }
          break;
        case "0018":
          if (!doc.GetFilterMgr()?.IsUserModifiedFilters()) {
            wsMgr.DoProgress(appstr("Loading chart"));
            doc.DoChartDataQuery(null, null, false, true);
            return;
          }

          var alternativeTopboxes = response["chartData"];
          switch (alternativeTopboxes.length) {
            case 0:
              // Reload the chart without the filters and let the local filter logic deal with it.
              wsMgr.DoProgress(appstr("Loading chart"));
              doc.DoChartDataQuery(null, null, false, true);
              return;
            case 1:
              var altTopboxInfo = alternativeTopboxes[0];
              var recordIdKey = doc.GetRecordIDField().GetFieldName();
              wsMgr.DoProgress(appstr("Loading chart"));
              doc.DoChartDataQuery(altTopboxInfo[recordIdKey]);
              break;
            default:
              wsMgr.BroadcastTopic("AlternateTopboxes", alternativeTopboxes);
              break;
          }
          return;
        case "0030":
          if (doc.IsServerChart()) {
            break;
          }

          // This will occur when the base chart associated with the live chart has been removed.
          alertHeader = appstr("Could not open custom chart");
          alertMessage = appstr("livechartnotfound");

          // Forces system to fallback on launch chart
          var docMgr = doc.GetDocMgr();
          var iWiz = docMgr.GetIWizDataInteractive();
          iWiz.SetBaseChartID(null);

          break;
        case 6000:
          alertMessage = appstr("rlsuserrecordnotfound");
          break;
        default:
          details = appstr("serverqueryerr");
          break;
      }
      if (!doc.IsServerChart() && !Globals._publishRef) {
        // Custom chart RFC failed (e.g. base chart inaccessible to user
        // — case 0030). doc.GetChartID() points at the unreachable
        // base, so SwitchToMasterChart with that ID can't recover.
        // Centralized helper picks a known-reachable target via the
        // same decision tree FinishLogin uses on cold start.
        wsMgr.FallbackToSafeChart();
      }

      if (!mappingAlertFired) {
        if (alertMessage == null) {
          alertMessage = gUseBannerErrorAlert ? (!!details ? details : r) : r + "\n\n" + details;
        }

        var w = new QAlert();
        w.InitAlert(
          alertHeader,
          alertMessage,
          null,
          wsMgr,
          true,
          undefined,
          gUseBannerErrorAlert ? { bannerErrorAlert: true } : undefined
        );
      }
    }
    wsMgr.fChartLoadPending = false;
    wsMgr.EndProgress();
  }
}
